import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  message,
  Alert,
  Statistic,
  Tabs,
  Form,
  Input,
} from 'antd';
import {
  HeartOutlined,
  ApiOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SyncOutlined,
  UserOutlined,
  LockOutlined,
  CreditCardOutlined,
  MailOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { dashboardService } from '../services/dashboardService';
import { subscriptionPlansService } from '../services/subscriptionPlansService';
import { transactionService } from '../services/transactionService';
import { authService } from '../services/authService';
import BillingConfig from './BillingConfig';
import BrandingTab from './BrandingTab';

const { Title, Text } = Typography;

const StatusPill = ({ ok, label }) => (
  <Tag color={ok ? 'success' : 'error'} style={{ marginInlineEnd: 0 }}>
    {ok ? '● Healthy' : '● Down'} {label}
  </Tag>
);

// ---- Tab: Profile ---------------------------------------------------------
const ProfileTab = ({ adminInfo }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      await authService.changePassword(values.currentPassword, values.newPassword);
      message.success('Password updated successfully');
      form.resetFields();
    } catch (err) {
      message.error(err.message || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card title={<span><SafetyOutlined /> Signed-in Super Admin</span>} bordered={false}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Email">{adminInfo?.email || 'super-admin'}</Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag color="purple">{adminInfo?.role || 'super_admin'}</Tag>
            </Descriptions.Item>
            {adminInfo?.lastLogin && (
              <Descriptions.Item label="Last login">
                {new Date(adminInfo.lastLogin).toLocaleString()}
              </Descriptions.Item>
            )}
          </Descriptions>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Tenant-scoped settings (AI provider, WhatsApp, billing plan) are configured per-tenant under Organizations → click an org → Settings tabs."
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title={<span><LockOutlined /> Change Password</span>} bordered={false}>
          <Form layout="vertical" form={form} onFinish={onFinish}>
            <Form.Item
              name="currentPassword"
              label="Current Password"
              rules={[{ required: true, message: 'Enter current password' }, { min: 8 }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[{ required: true, message: 'Enter new password' }, { min: 8 }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm New Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Confirm new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Update Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

// ---- Tab: System ----------------------------------------------------------
const SystemTab = () => {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [syncing, setSyncing] = useState({ razorpay: false, transactions: false });

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await dashboardService.getHealth();
      setHealth(res.data || res);
    } catch {
      message.error('Failed to load system health');
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  const handleSyncRazorpayAll = async () => {
    setSyncing((s) => ({ ...s, razorpay: true }));
    try {
      const res = await subscriptionPlansService.syncAllPlansWithRazorpay();
      message.success(res?.message || 'Razorpay plans synced');
    } catch (err) {
      message.error(err.response?.data?.message || 'Razorpay sync failed');
    } finally {
      setSyncing((s) => ({ ...s, razorpay: false }));
    }
  };

  const handleSyncTransactions = async () => {
    setSyncing((s) => ({ ...s, transactions: true }));
    try {
      const res = await transactionService.syncTransactions();
      message.success(res?.message || 'Transactions synced');
    } catch (err) {
      message.error(err.response?.data?.message || 'Transaction sync failed');
    } finally {
      setSyncing((s) => ({ ...s, transactions: false }));
    }
  };

  const dbOk = health?.database?.status === 'connected' || health?.database?.connected;
  const memoryMb = health?.memory ? Math.round((health.memory.used || 0) / 1024 / 1024) : null;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={14}>
        <Card
          title={<span><HeartOutlined /> System Health</span>}
          bordered={false}
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={healthLoading}
              onClick={fetchHealth}
            >
              Refresh
            </Button>
          }
        >
          {healthLoading && !health ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : (
            <>
              <Space size={[8, 12]} wrap>
                <StatusPill ok={dbOk} label="MongoDB" />
                <StatusPill ok={!!health?.uptime} label="Server" />
              </Space>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic
                    title="Uptime"
                    value={health?.uptime ? Math.round(health.uptime / 60) : 0}
                    suffix="min"
                    prefix={<DatabaseOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Memory"
                    value={memoryMb || 0}
                    suffix="MB"
                    prefix={<ApiOutlined />}
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Card title={<span><SyncOutlined /> Maintenance</span>} bordered={false}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card type="inner" title="Razorpay sync" size="small">
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Pull plan + price changes from Razorpay into the local subscription plan list.
              </Text>
              <Button
                type="primary"
                icon={<SyncOutlined spin={syncing.razorpay} />}
                loading={syncing.razorpay}
                onClick={handleSyncRazorpayAll}
              >
                Sync all plans
              </Button>
            </Card>
            <Card type="inner" title="Transaction sync" size="small">
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Re-fetch the latest payment activity from Razorpay and reconcile.
              </Text>
              <Button
                icon={<SyncOutlined spin={syncing.transactions} />}
                loading={syncing.transactions}
                onClick={handleSyncTransactions}
              >
                Sync transactions
              </Button>
            </Card>
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

// ---- Tab: Email (read-only — SMTP comes from server env) ------------------
const EmailTab = () => (
  <Row gutter={[16, 16]}>
    <Col xs={24}>
      <Card title={<span><MailOutlined /> Email & Notifications</span>} bordered={false}>
        <Alert
          type="info"
          showIcon
          message="SMTP credentials are read from server environment variables (SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASS, SMTP_FROM). Update them on the host and restart the admin server to apply."
          style={{ marginBottom: 16 }}
        />
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Central admin recipient">
            <Text code>SUPER_ADMIN_EMAIL</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Subscription notify list (CC)">
            <Text code>BILLING_NOTIFY_EMAILS</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Finance copy (with invoice attached)">
            <Text code>QQ_FINANCE_EMAIL</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Outbound transport">
            <Text code>SMTP_HOST / SMTP_USER</Text>
          </Descriptions.Item>
        </Descriptions>
        <Alert
          type="success"
          showIcon
          style={{ marginTop: 16 }}
          message="Every captured Razorpay payment automatically emails the central admin and the subscription notify list, with the invoice PDF attached for finance."
        />
      </Card>
    </Col>
  </Row>
);

const Settings = () => {
  const [adminInfo] = useState(() => {
    try {
      const raw = localStorage.getItem('super_admin_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  return (
    <div className="space-y-6" style={{ padding: '0 4px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          Platform Settings
        </Title>
        <Text type="secondary">
          Global super-admin controls. Per-tenant settings live inside each Organization's detail page.
        </Text>
      </div>

      <Tabs
        defaultActiveKey="profile"
        items={[
          {
            key: 'profile',
            label: (
              <span>
                <UserOutlined /> Profile
              </span>
            ),
            children: <ProfileTab adminInfo={adminInfo} />,
          },
          {
            key: 'system',
            label: (
              <span>
                <HeartOutlined /> System
              </span>
            ),
            children: <SystemTab />,
          },
          {
            key: 'billing',
            label: (
              <span>
                <CreditCardOutlined /> Billing
              </span>
            ),
            children: <BillingConfig />,
          },
          {
            key: 'email',
            label: (
              <span>
                <MailOutlined /> Email
              </span>
            ),
            children: <EmailTab />,
          },
          {
            key: 'branding',
            label: (
              <span>
                <SettingOutlined /> Branding
              </span>
            ),
            children: <BrandingTab />,
          },
        ]}
      />
    </div>
  );
};

export default Settings;
